import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ContentCopy as ContentCopyIcon,
  Edit as EditIcon,
  EmailOutlined as EmailOutlinedIcon,
  Layers as LayersIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Send as SendIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { API_ORIGIN, publicEstimateUrl } from '../api';
import apiService from '../services/apiService';
import SendMessageDialog from '../components/SendMessageDialog';
import { getUiPreferences, patchSavedFiltersKey } from '../userPreferencesApi';

function readInitialEstimateFilters() {
  try {
    const e = getUiPreferences().savedFilters?.estimates;
    if (!e || typeof e !== 'object') return { q: '', status: '', page: 1 };
    return {
      q: typeof e.filterQ === 'string' ? e.filterQ : '',
      status: typeof e.filterStatus === 'string' ? e.filterStatus : '',
      page: Number.isFinite(Number(e.listPage)) && Number(e.listPage) > 0 ? Number(e.listPage) : 1,
    };
  } catch {
    return { q: '', status: '', page: 1 };
  }
}

function money(v, currency) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '-';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'GBP' }).format(n);
  } catch {
    return `${n.toFixed(2)} ${currency || ''}`.trim();
  }
}

const defaultEstimateForm = () => ({
  client_id: null,
  client_name: '',
  client_email: '',
  client_phone: '',
  client_address: '',
  client_company: '',
  currency: 'GBP',
  valid_until: '',
  notes: '',
  lines: [{ title: 'Service', description: '', quantity: 1, unit_price: 0 }],
});

function normalizeClientName(s) {
  return (s || '').trim().toLowerCase();
}

function getClientOptionLabel(option) {
  if (typeof option === 'string') return option;
  if (!option) return '';
  const parts = [`#${option.id}`, option.name || ''].filter(Boolean);
  return parts.join(' · ');
}

function shouldClearClientIdOnText(text, clientId, options) {
  if (clientId === '' || clientId == null) return false;
  const opt = options.find((o) => String(o.id) === String(clientId));
  if (!opt) return false;
  return normalizeClientName(opt.name) !== normalizeClientName(text);
}

function mergeClientRecordIntoList(list, rec) {
  const rows = Array.isArray(list) ? list : [];
  if (!rec?.id) return rows;
  if (rows.some((c) => Number(c.id) === Number(rec.id))) return rows;
  return [rec, ...rows];
}

function buildEstimatePayload(form, { forUpdate }) {
  const linesPayload = (form.lines || []).map((l, idx) => {
    const row = {
      sort_order: idx,
      title: l.title,
      description: l.description || undefined,
      quantity: l.quantity != null ? Number(l.quantity) : 1,
      unit_price: l.unit_price != null ? Number(l.unit_price) : 0,
    };
    if (l.id != null) row.id = l.id;
    return row;
  });
  const payload = {
    currency: form.currency,
    notes: form.notes || undefined,
    lines: linesPayload,
    client_name: form.client_name || undefined,
    client_email: form.client_email || undefined,
    client_phone: form.client_phone || undefined,
    client_address: form.client_address || undefined,
    client_company: form.client_company || undefined,
  };
  const vu = (form.valid_until && String(form.valid_until).trim() !== '') ? String(form.valid_until).trim() : null;
  if (forUpdate) {
    payload.valid_until = vu;
    payload.client_id = form.client_id != null && form.client_id !== '' ? Number(form.client_id) : null;
  } else {
    if (vu) payload.valid_until = vu;
    if (form.client_id != null && form.client_id !== '') {
      payload.client_id = Number(form.client_id);
    }
  }
  return payload;
}

export default function Estimates() {
  const initFilters = readInitialEstimateFilters();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);

  const [estimateDialogOpen, setEstimateDialogOpen] = useState(false);
  const [sendMessageOpen, setSendMessageOpen] = useState(false);
  const [sendMessageEstimate, setSendMessageEstimate] = useState(null);
  const [editingEstimateId, setEditingEstimateId] = useState(null);
  const [saveBusy, setSaveBusy] = useState(false);
  const [editOpeningId, setEditOpeningId] = useState(null);
  const [form, setForm] = useState(defaultEstimateForm);

  const [clientSearchOptions, setClientSearchOptions] = useState([]);
  const [clientSearchLoading, setClientSearchLoading] = useState(false);
  const clientSearchDebounceRef = useRef(null);
  const clientSearchSeqRef = useRef(0);
  const clientMergeForListRef = useRef(null);

  const [catalogOpen, setCatalogOpen] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogQ, setCatalogQ] = useState('');
  const [catalogItems, setCatalogItems] = useState([]);

  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [templateList, setTemplateList] = useState([]);
  const [templateListLoading, setTemplateListLoading] = useState(false);

  const [shareBusyId, setShareBusyId] = useState(null);
  const [shareUrlById, setShareUrlById] = useState({});
  const [pdfBusyId, setPdfBusyId] = useState(null);

  const [messageDraftOpen, setMessageDraftOpen] = useState(false);
  const [messageDraftRow, setMessageDraftRow] = useState(null);
  const [messageTemplates, setMessageTemplates] = useState([]);
  const [messageTemplatesLoading, setMessageTemplatesLoading] = useState(false);
  const [messageDraftTemplateId, setMessageDraftTemplateId] = useState('');
  const [messageDraftPreview, setMessageDraftPreview] = useState('');
  const [messageDraftBusy, setMessageDraftBusy] = useState(false);

  const [filterQInput, setFilterQInput] = useState(initFilters.q);
  const [filterQ, setFilterQ] = useState(initFilters.q);
  const [filterStatus, setFilterStatus] = useState(initFilters.status);
  const [listPage, setListPage] = useState(initFilters.page);
  const [listLastPage, setListLastPage] = useState(1);
  const skipEstimateFilterDebounceOnce = useRef(true);

  const totalDraft = useMemo(() => {
    const subtotal = (form.lines || []).reduce((acc, l) => {
      const q = Number(l.quantity || 0);
      const u = Number(l.unit_price || 0);
      return acc + (Number.isFinite(q) && Number.isFinite(u) ? q * u : 0);
    }, 0);
    return Math.round(subtotal * 100) / 100;
  }, [form.lines]);

  const fetchEstimates = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const qs = new URLSearchParams({ per_page: '20', page: String(listPage) });
      const qTrim = filterQ.trim();
      if (qTrim) qs.set('q', qTrim);
      if (filterStatus) qs.set('status', filterStatus);
      const res = await apiService.get(`estimates?${qs}`);
      setItems(Array.isArray(res?.data) ? res.data : []);
      const meta = res?.meta;
      if (meta) {
        setListLastPage(Math.max(1, Number(meta.last_page) || 1));
      } else {
        setListLastPage(1);
      }
    } catch (e) {
      setError(e.message || 'Failed to load estimates');
    } finally {
      setLoading(false);
    }
  }, [filterQ, filterStatus, listPage]);

  useEffect(() => {
    if (skipEstimateFilterDebounceOnce.current) {
      skipEstimateFilterDebounceOnce.current = false;
      return undefined;
    }
    const t = setTimeout(() => {
      setFilterQ(filterQInput);
      setListPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [filterQInput]);

  useEffect(() => {
    fetchEstimates();
  }, [fetchEstimates]);

  useEffect(() => {
    const t = setTimeout(() => {
      patchSavedFiltersKey('estimates', {
        filterQ: filterQInput,
        filterStatus,
        listPage,
      }).catch(() => {});
    }, 900);
    return () => clearTimeout(t);
  }, [filterQInput, filterStatus, listPage]);

  const runClientFetch = async (rawQuery) => {
    const q = (rawQuery || '').trim();
    const seq = ++clientSearchSeqRef.current;
    setClientSearchLoading(true);
    try {
      const qs = new URLSearchParams({ search: '1', limit: '50' });
      if (q) qs.set('q', q);
      const raw = await apiService.get(`clients?${qs}`);
      const list = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
      if (seq !== clientSearchSeqRef.current) return;
      setClientSearchOptions(mergeClientRecordIntoList(list, clientMergeForListRef.current));
    } catch {
      if (seq !== clientSearchSeqRef.current) return;
      setClientSearchOptions(mergeClientRecordIntoList([], clientMergeForListRef.current));
    } finally {
      if (seq === clientSearchSeqRef.current) {
        setClientSearchLoading(false);
      }
    }
  };

  const fetchClientsForEstimateForm = (rawQuery) => {
    const q = (rawQuery || '').trim();
    clearTimeout(clientSearchDebounceRef.current);
    if (q === '') {
      void runClientFetch('');
      return;
    }
    clientSearchDebounceRef.current = setTimeout(() => {
      void runClientFetch(rawQuery);
    }, 200);
  };

  function openCreateDialog() {
    clientMergeForListRef.current = null;
    setEditingEstimateId(null);
    setForm(defaultEstimateForm());
    setClientSearchOptions([]);
    setEstimateDialogOpen(true);
    fetchClientsForEstimateForm('');
  }

  function closeEstimateDialog() {
    if (saveBusy) return;
    setEstimateDialogOpen(false);
    setEditingEstimateId(null);
    setForm(defaultEstimateForm());
    setClientSearchOptions([]);
    clientMergeForListRef.current = null;
  }

  async function openEditDialog(id) {
    setError('');
    setEditOpeningId(id);
    try {
      const e = await apiService.get(`estimates/${id}`);
      clientMergeForListRef.current = e.client || null;
      setForm({
        client_id: e.client_id ?? null,
        client_name: e.client_name || e.client?.name || '',
        client_email: e.client_email || e.client?.email || '',
        client_phone: e.client_phone || e.client?.phone || '',
        client_address: e.client_address || e.client?.address || '',
        client_company: e.client_company || e.client?.company || '',
        currency: e.currency || 'GBP',
        valid_until: e.valid_until ? String(e.valid_until).slice(0, 10) : '',
        notes: e.notes || '',
        lines:
          (e.lines || []).length > 0
            ? e.lines.map((l) => ({
                id: l.id,
                title: l.title || '',
                description: l.description || '',
                quantity: l.quantity,
                unit_price: l.unit_price,
              }))
            : [{ title: 'Service', description: '', quantity: 1, unit_price: 0 }],
      });
      setEditingEstimateId(id);
      setEstimateDialogOpen(true);
      fetchClientsForEstimateForm('');
    } catch (err) {
      setError(err.message || 'Failed to load estimate');
    } finally {
      setEditOpeningId(null);
    }
  }

  async function fetchCatalog(query = '') {
    setCatalogLoading(true);
    try {
      const qs = query.trim() ? `?active=1&q=${encodeURIComponent(query.trim())}` : '?active=1';
      const res = await apiService.get(`price-book${qs}`);
      setCatalogItems(Array.isArray(res) ? res : []);
    } catch {
      setCatalogItems([]);
    } finally {
      setCatalogLoading(false);
    }
  }

  function updateLine(idx, patch) {
    setForm((f) => {
      const lines = [...(f.lines || [])];
      lines[idx] = { ...lines[idx], ...patch };
      return { ...f, lines };
    });
  }

  function addLine() {
    setForm((f) => ({ ...f, lines: [...(f.lines || []), { title: '', description: '', quantity: 1, unit_price: 0 }] }));
  }

  function removeLine(idx) {
    setForm((f) => {
      const lines = [...(f.lines || [])];
      lines.splice(idx, 1);
      return { ...f, lines: lines.length ? lines : [{ title: 'Service', description: '', quantity: 1, unit_price: 0 }] };
    });
  }

  async function handleSaveEstimate() {
    setError('');
    setSaveBusy(true);
    try {
      if (editingEstimateId) {
        await apiService.put(`estimates/${editingEstimateId}`, buildEstimatePayload(form, { forUpdate: true }));
      } else {
        await apiService.post('estimates', buildEstimatePayload(form, { forUpdate: false }));
      }
      setEstimateDialogOpen(false);
      setEditingEstimateId(null);
      setForm(defaultEstimateForm());
      setClientSearchOptions([]);
      clientMergeForListRef.current = null;
      await fetchEstimates();
    } catch (e) {
      setError(e.message || 'Failed to save estimate');
    } finally {
      setSaveBusy(false);
    }
  }

  function addCatalogItemToLines(item) {
    setForm((f) => ({
      ...f,
      currency: f.currency || item.currency || 'GBP',
      lines: [
        ...(f.lines || []),
        {
          title: item.name || 'Service',
          description: item.description || '',
          quantity: 1,
          unit_price: Number(item.unit_price || 0),
        },
      ],
    }));
    setCatalogOpen(false);
    setCatalogQ('');
  }

  async function openTemplateDialog() {
    setTemplateDialogOpen(true);
    setTemplateListLoading(true);
    try {
      const res = await apiService.get('estimate-templates');
      setTemplateList(Array.isArray(res) ? res : []);
    } catch {
      setTemplateList([]);
    } finally {
      setTemplateListLoading(false);
    }
  }

  async function applyTemplateFromApi(templateId, mode) {
    setError('');
    try {
      const t = await apiService.get(`estimate-templates/${templateId}`);
      const mapped = (t.lines || []).map((l) => ({
        title: l.title || '',
        description: l.description || '',
        quantity: l.quantity ?? 1,
        unit_price: l.unit_price ?? 0,
      }));
      setForm((f) => {
        if (mode === 'replace') {
          return {
            ...f,
            currency: t.currency || f.currency || 'GBP',
            lines: mapped.length
              ? mapped
              : [{ title: 'Service', description: '', quantity: 1, unit_price: 0 }],
          };
        }
        return {
          ...f,
          currency: f.currency || t.currency || 'GBP',
          lines: [...(f.lines || []), ...mapped],
        };
      });
      setTemplateDialogOpen(false);
    } catch (e) {
      setError(e.message || 'Failed to load template');
    }
  }

  async function handleSend(estimateId) {
    setError('');
    setShareBusyId(estimateId);
    try {
      const res = await apiService.post(`estimates/${estimateId}/send`, {});
      const url = res?.share_url;
      if (url) {
        setShareUrlById((m) => ({ ...m, [estimateId]: url }));
        try {
          await navigator.clipboard.writeText(url);
        } catch {
          // ignore clipboard failures
        }
      }
      await fetchEstimates();
    } catch (e) {
      setError(e.message || 'Failed to generate share link');
    } finally {
      setShareBusyId(null);
    }
  }

  async function copyUrl(estimateId) {
    const url = shareUrlById[estimateId];
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // ignore
    }
  }

  function resolveShareUrlForRow(row) {
    if (!row) return '';
    const session = shareUrlById[row.id];
    if (session) return session;
    return publicEstimateUrl(row.public_token);
  }

  function formatValidUntilForDraft(v) {
    if (v == null || v === '') return '';
    const s = String(v);
    return s.includes('T') ? s.split('T')[0] : s;
  }

  async function runMessageDraftRender(row, templateId) {
    if (!row || !templateId) return;
    setMessageDraftBusy(true);
    setError('');
    try {
      const share = resolveShareUrlForRow(row);
      const res = await apiService.post(`message-templates/${templateId}/render`, {
        client_name: row.client_name || row.client?.name || '',
        share_url: share,
        estimate_id: String(row.id),
        total: row.total != null ? String(row.total) : '',
        currency: row.currency || 'GBP',
        valid_until: formatValidUntilForDraft(row.valid_until),
      });
      setMessageDraftPreview(res.body || '');
    } catch (e) {
      setError(e.message || 'Failed to render template');
      setMessageDraftPreview('');
    } finally {
      setMessageDraftBusy(false);
    }
  }

  async function openMessageDraft(row) {
    setMessageDraftRow(row);
    setMessageDraftTemplateId('');
    setMessageDraftPreview('');
    setMessageDraftOpen(true);
    setMessageTemplatesLoading(true);
    try {
      const res = await apiService.get('message-templates');
      const list = Array.isArray(res) ? res : [];
      setMessageTemplates(list);
      const first = list.find((t) => t.channel === 'email') || list[0];
      if (first) {
        const tid = String(first.id);
        setMessageDraftTemplateId(tid);
        await runMessageDraftRender(row, tid);
      }
    } catch {
      setMessageTemplates([]);
    } finally {
      setMessageTemplatesLoading(false);
    }
  }

  function closeMessageDraft() {
    setMessageDraftOpen(false);
    setMessageDraftRow(null);
    setMessageDraftTemplateId('');
    setMessageDraftPreview('');
  }

  async function copyMessageDraft() {
    if (!messageDraftPreview) return;
    try {
      await navigator.clipboard.writeText(messageDraftPreview);
    } catch {
      // ignore
    }
  }

  async function downloadEstimatePdf(estimateId) {
    setError('');
    setPdfBusyId(estimateId);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_ORIGIN}/api/estimates/${estimateId}/pdf`, {
        headers: {
          Accept: 'application/pdf',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!res.ok) {
        let msg = 'PDF download failed';
        try {
          const j = await res.json();
          msg = j.message || j.error || msg;
        } catch {
          /* response may not be JSON */
        }
        throw new Error(msg);
      }
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = `estimate-${estimateId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
    } catch (e) {
      setError(e.message || 'PDF download failed');
    } finally {
      setPdfBusyId(null);
    }
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="h4" sx={{ fontWeight: 800 }}>
          Estimates
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog} sx={{ textTransform: 'none' }}>
          New estimate
        </Button>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          mb: 2,
          alignItems: { xs: 'stretch', sm: 'center' },
          flexWrap: 'wrap',
        }}
      >
        <TextField
          size="small"
          placeholder="Search name, email, #estimate, CRM id…"
          value={filterQInput}
          onChange={(e) => setFilterQInput(e.target.value)}
          sx={{ flex: { sm: '1 1 240px' }, minWidth: { sm: 200 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="estimates-status-filter">Status</InputLabel>
          <Select
            labelId="estimates-status-filter"
            label="Status"
            value={filterStatus}
            onChange={(e) => {
              setFilterStatus(e.target.value);
              setListPage(1);
            }}
            renderValue={(v) => (v === '' ? 'All' : String(v))}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="sent">Sent</MenuItem>
            <MenuItem value="accepted">Accepted</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {items.length === 0 ? (
            <Card>
              <CardContent>
                <Typography sx={{ fontWeight: 700 }}>
                  {filterQ.trim() || filterStatus ? 'No estimates match your filters.' : 'No estimates yet.'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {filterQ.trim() || filterStatus
                    ? 'Try clearing search or status.'
                    : 'Create your first estimate and send a link to the client.'}
                </Typography>
              </CardContent>
            </Card>
          ) : (
            items.map((it) => (
              <Card key={it.id} sx={{ boxShadow: '0 1px 2px rgba(0,0,0,0.08)' }}>
                <CardContent sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { sm: 'center' } }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontWeight: 800 }}>
                        #{it.id} · {it.client_name || it.client?.name || 'Client'}
                      </Typography>
                      {it.client_id ? (
                        <Chip
                          size="small"
                          component={RouterLink}
                          to={`/clients?id=${it.client_id}`}
                          label={`CRM #${it.client_id}`}
                          clickable
                          variant="outlined"
                          sx={{ height: 22, fontWeight: 700, textDecoration: 'none' }}
                        />
                      ) : null}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Status: <b>{it.status}</b> · Total: <b>{money(it.total, it.currency)}</b>
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={editOpeningId === it.id ? <CircularProgress size={14} /> : <EditIcon />}
                      disabled={editOpeningId === it.id}
                      onClick={() => openEditDialog(it.id)}
                      sx={{ textTransform: 'none' }}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={pdfBusyId === it.id ? <CircularProgress size={14} /> : <PictureAsPdfIcon />}
                      disabled={pdfBusyId === it.id}
                      onClick={() => downloadEstimatePdf(it.id)}
                      sx={{ textTransform: 'none' }}
                    >
                      PDF
                    </Button>
                    {shareUrlById[it.id] && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ContentCopyIcon />}
                        onClick={() => copyUrl(it.id)}
                        sx={{ textTransform: 'none' }}
                      >
                        Copy link
                      </Button>
                    )}
                    <Tooltip title={resolveShareUrlForRow(it) ? '' : 'Generate a share link first'}>
                      <span>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<EmailOutlinedIcon />}
                          disabled={!resolveShareUrlForRow(it)}
                          onClick={() => openMessageDraft(it)}
                          sx={{ textTransform: 'none' }}
                        >
                          Draft message
                        </Button>
                      </span>
                    </Tooltip>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={shareBusyId === it.id ? null : <SendIcon />}
                      disabled={shareBusyId === it.id}
                      onClick={() => handleSend(it.id)}
                      sx={{ textTransform: 'none' }}
                    >
                      {shareBusyId === it.id ? 'Generating…' : 'Share link'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            ))
          )}
          {!loading && listLastPage > 1 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
              <Pagination
                color="primary"
                page={listPage}
                count={listLastPage}
                onChange={(e, p) => setListPage(p)}
                size="small"
                showFirstButton
                showLastButton
              />
            </Box>
          ) : null}
        </Box>
      )}

      <Dialog open={estimateDialogOpen} onClose={closeEstimateDialog} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 800 }}>{editingEstimateId ? `Edit estimate #${editingEstimateId}` : 'New estimate'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 1 }}>
            <Box sx={{ gridColumn: { xs: '1 / -1', sm: '1 / -1' } }}>
              <Autocomplete
                freeSolo
                openOnFocus
                options={clientSearchOptions}
                filterOptions={(options) => options}
                loading={clientSearchLoading}
                getOptionLabel={getClientOptionLabel}
                isOptionEqualToValue={(a, b) =>
                  typeof a === 'object' &&
                  typeof b === 'object' &&
                  a?.id != null &&
                  b?.id != null &&
                  Number(a.id) === Number(b.id)
                }
                inputValue={form.client_name}
                onInputChange={(e, value, reason) => {
                  if (reason === 'reset') return;
                  if (reason === 'clear') {
                    setForm((f) => ({
                      ...f,
                      client_id: null,
                      client_name: '',
                      client_email: '',
                      client_phone: '',
                      client_address: '',
                      client_company: '',
                    }));
                    fetchClientsForEstimateForm('');
                    return;
                  }
                  if (reason === 'input') {
                    setForm((f) => ({
                      ...f,
                      client_name: value,
                      client_id: shouldClearClientIdOnText(value, f.client_id, clientSearchOptions) ? null : f.client_id,
                    }));
                    fetchClientsForEstimateForm(value);
                  }
                }}
                onChange={(e, value) => {
                  if (value == null) {
                    setForm((f) => ({
                      ...f,
                      client_id: null,
                      client_name: '',
                      client_email: '',
                      client_phone: '',
                      client_address: '',
                      client_company: '',
                    }));
                    return;
                  }
                  if (typeof value === 'object' && value.id != null) {
                    setForm((f) => ({
                      ...f,
                      client_id: value.id,
                      client_name: value.name || '',
                      client_email: value.email || f.client_email,
                      client_phone: value.phone || '',
                      client_address: value.address || '',
                      client_company: value.company || '',
                    }));
                    return;
                  }
                  if (typeof value === 'string') {
                    setForm((f) => ({
                      ...f,
                      client_name: value,
                      client_id: shouldClearClientIdOnText(value, f.client_id, clientSearchOptions) ? null : f.client_id,
                    }));
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Client (search CRM or type name)"
                    placeholder="Name, phone, email…"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {clientSearchLoading ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Box>
            <TextField
              label="Client email"
              value={form.client_email}
              onChange={(e) => setForm((f) => ({ ...f, client_email: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Phone"
              value={form.client_phone}
              onChange={(e) => setForm((f) => ({ ...f, client_phone: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Company"
              value={form.client_company}
              onChange={(e) => setForm((f) => ({ ...f, client_company: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Currency"
              value={form.currency}
              onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Valid until"
              type="date"
              value={form.valid_until || ''}
              onChange={(e) => setForm((f) => ({ ...f, valid_until: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Address"
              value={form.client_address}
              onChange={(e) => setForm((f) => ({ ...f, client_address: e.target.value }))}
              fullWidth
              sx={{ gridColumn: { xs: '1 / -1', sm: '1 / -1' } }}
            />
            <TextField
              label="Notes"
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              fullWidth
              multiline
              minRows={1}
              sx={{ gridColumn: { xs: '1 / -1', sm: '1 / -1' } }}
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography sx={{ fontWeight: 800, mb: 1 }}>Lines</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<SearchIcon />}
                onClick={() => {
                  setCatalogOpen(true);
                  fetchCatalog('');
                }}
                sx={{ textTransform: 'none' }}
              >
                Add from price book
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<LayersIcon />}
                onClick={openTemplateDialog}
                sx={{ textTransform: 'none' }}
              >
                From template
              </Button>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {(form.lines || []).map((l, idx) => (
              <Card key={l.id != null ? `id-${l.id}` : `idx-${idx}`} variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr 1fr' }, gap: 1.5, alignItems: 'center' }}>
                    <TextField
                      label="Title"
                      value={l.title}
                      onChange={(e) => updateLine(idx, { title: e.target.value })}
                      fullWidth
                    />
                    <TextField
                      label="Qty"
                      type="number"
                      value={l.quantity}
                      onChange={(e) => updateLine(idx, { quantity: e.target.value })}
                      fullWidth
                      inputProps={{ step: '0.01', min: 0 }}
                    />
                    <TextField
                      label="Unit price"
                      type="number"
                      value={l.unit_price}
                      onChange={(e) => updateLine(idx, { unit_price: e.target.value })}
                      fullWidth
                      inputProps={{ step: '0.01', min: 0 }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <TextField
                      label="Description"
                      value={l.description}
                      onChange={(e) => updateLine(idx, { description: e.target.value })}
                      fullWidth
                    />
                    <IconButton aria-label="Remove line" onClick={() => removeLine(idx)} size="small">
                      ×
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            ))}

            <Button variant="outlined" onClick={addLine} sx={{ textTransform: 'none', alignSelf: 'flex-start' }}>
              Add line
            </Button>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Typography sx={{ fontWeight: 900 }}>
              Total: {money(totalDraft, form.currency)}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {editingEstimateId && (form.client_phone || form.client_email) ? (
            <Button
              variant="outlined"
              startIcon={<SendIcon />}
              onClick={() => {
                setSendMessageEstimate({
                  id: editingEstimateId,
                  client_phone: form.client_phone,
                  client_email: form.client_email,
                });
                setSendMessageOpen(true);
              }}
              sx={{ textTransform: 'none', mr: 'auto' }}
            >
              Send message
            </Button>
          ) : null}
          <Button onClick={closeEstimateDialog} disabled={saveBusy} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveEstimate} disabled={saveBusy} sx={{ textTransform: 'none' }}>
            {saveBusy ? 'Saving…' : editingEstimateId ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <SendMessageDialog
        open={sendMessageOpen}
        onClose={() => {
          setSendMessageOpen(false);
          setSendMessageEstimate(null);
        }}
        defaultChannel={sendMessageEstimate?.client_phone ? 'sms' : 'email'}
        defaultTo={sendMessageEstimate?.client_phone || sendMessageEstimate?.client_email || ''}
        subjectType="estimate"
        subjectId={sendMessageEstimate?.id}
        suggestEstimateId={sendMessageEstimate?.id}
      />

      <Dialog open={catalogOpen} onClose={() => setCatalogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 900 }}>Price book</DialogTitle>
        <DialogContent>
          <TextField
            size="small"
            placeholder="Search…"
            value={catalogQ}
            onChange={(e) => {
              const v = e.target.value;
              setCatalogQ(v);
              fetchCatalog(v);
            }}
            fullWidth
            sx={{ mt: 1, mb: 2 }}
          />

          {catalogLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={22} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {catalogItems.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No items found. Add items in “Price book”.
                </Typography>
              ) : (
                catalogItems.map((it) => (
                  <Card key={it.id} variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent sx={{ p: 2, display: 'flex', gap: 1.5, alignItems: 'center' }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 900 }}>{it.name}</Typography>
                        {it.description && (
                          <Typography variant="body2" color="text.secondary">
                            {it.description}
                          </Typography>
                        )}
                      </Box>
                      <Button variant="contained" size="small" onClick={() => addCatalogItemToLines(it)} sx={{ textTransform: 'none' }}>
                        Add
                      </Button>
                    </CardContent>
                  </Card>
                ))
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCatalogOpen(false)} sx={{ textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={templateDialogOpen} onClose={() => setTemplateDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 900 }}>Load from template</DialogTitle>
        <DialogContent>
          {templateListLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={22} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
              {templateList.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No templates yet. Create them under <b>Estimate templates</b> in the menu.
                </Typography>
              ) : (
                templateList.map((t) => (
                  <Card key={t.id} variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                      <Typography sx={{ fontWeight: 800 }}>{t.name}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        {t.currency || 'GBP'} · {t.lines_count ?? 0} line(s)
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button size="small" variant="outlined" onClick={() => applyTemplateFromApi(t.id, 'append')} sx={{ textTransform: 'none' }}>
                          Append lines
                        </Button>
                        <Button size="small" variant="contained" onClick={() => applyTemplateFromApi(t.id, 'replace')} sx={{ textTransform: 'none' }}>
                          Replace lines
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                ))
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setTemplateDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={messageDraftOpen} onClose={() => !messageDraftBusy && closeMessageDraft()} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 800 }}>Message draft</DialogTitle>
        <DialogContent>
          {messageTemplatesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : messageTemplates.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              No active templates. Add them under <b>Message templates</b> in the menu.
            </Typography>
          ) : (
            <>
              <FormControl fullWidth sx={{ mt: 1 }}>
                <InputLabel id="msg-draft-template">Template</InputLabel>
                <Select
                  labelId="msg-draft-template"
                  label="Template"
                  value={messageDraftTemplateId}
                  onChange={(e) => {
                    const id = e.target.value;
                    setMessageDraftTemplateId(id);
                    if (messageDraftRow && id) {
                      runMessageDraftRender(messageDraftRow, id);
                    }
                  }}
                >
                  {messageTemplates.map((t) => (
                    <MenuItem key={t.id} value={String(t.id)}>
                      {t.name} ({t.channel === 'sms' ? 'SMS' : 'Email'})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ position: 'relative', mt: 2 }}>
                {messageDraftBusy ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <TextField
                    label="Preview"
                    value={messageDraftPreview}
                    fullWidth
                    multiline
                    minRows={10}
                    InputProps={{ readOnly: true }}
                  />
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  startIcon={<ContentCopyIcon />}
                  onClick={copyMessageDraft}
                  disabled={!messageDraftPreview || messageDraftBusy}
                  sx={{ textTransform: 'none' }}
                >
                  Copy text
                </Button>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeMessageDraft} disabled={messageDraftBusy} sx={{ textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

